import { MenuItem } from "@mui/material";
import Enumerable from "linq";
import { ChangeEvent, useEffect, useMemo, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RecipientSubTypeEnum } from "../../common/enums/RecipientSubTypeEnum";
import { RecipientTypeEnum } from "../../common/enums/RecipientTypeEnum";
import { getRecipientTypeName } from "../../common/utilities/GetRecipientTypeName";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { BindDialogReturn, DialogProps, DialogResult, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useCustomerAttribute } from "../../queries/attributes/UseCustomerAttribute";
import { useCheckRecipient } from "../../queries/recipients/UseCheckRecipient";
import { useRecipientReferenceData } from "../../queries/recipients/UseRecipientReferenceData";
import FormCheckbox from "../forms/form-checkbox/FormCheckbox";
import FormInput from "../forms/form-input/FormInput";
import FormPropertySection from "../forms/form-property-section/FormPropertySection";
import FormProperty from "../forms/form-property/FormProperty";
import FormSelect from "../forms/form-select/FormSelect";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { countries, recipientSubTypes, recipientTypes, toCheckRecipientParams, toFormValuesType, toReturnValues } from "./RecipientEditDialogUtils";
import { phoneNumberWithoutCountryCodeRegex } from "../../common/regex/RegEx";

export enum OpenMode {
    Add = 0,
    Edit = 1
}

export type RecipientEditDialogInitValues = {
    openMode: OpenMode;
    organizationID?: number;
    allowLinkToRecipientUser?: boolean; // link to an existing user or create an adhoc user
    allowCloneRecipientUsage: boolean;
} & RecipientEditDialogReturnValues;

export type RecipientEditDialogReturnValues = {
    recipientID: number;
    recipientType: RecipientTypeEnum;
    recipientSubType: RecipientSubTypeEnum;
    recipientTo: string;
    userID?: number;
    userFirstName?: string; // for adhoc user
    userLastName?: string;
    cloneRecipientID?: number;
};

export type RecipientEditDialogResult = DialogResult<RecipientEditDialogReturnValues>;
export type RecipientEditDialogProps = DialogProps<RecipientEditDialogInitValues, RecipientEditDialogResult>;

export type RecipientEditDialogFormValuesType = {
    id: number;
    type: RecipientTypeEnum;
    subType: RecipientSubTypeEnum;
    emailAddress?: string;
    countryCode?: string;
    phoneNumber?: string;
    cloneRecipientUsage?: boolean;
    cloneRecipientID?: number;

    linkToRecipientUser?: boolean;
    linkedRecipientUserID?: number;

    userFirstName?: string;
    userLastName?: string;
};

const defaultFormValues: RecipientEditDialogFormValuesType = {
    id: -1,
    type: RecipientTypeEnum.Email,
    subType: RecipientSubTypeEnum.Cell,
    emailAddress: undefined,
    countryCode: "us",
    phoneNumber: undefined,
    cloneRecipientUsage: false,
    cloneRecipientID: -1,

    linkToRecipientUser: false,
    linkedRecipientUserID: -1,
    userFirstName: undefined,
    userLastName: undefined
};

export default function RecipientEditDialog({
    open,
    onClose,
    values,
}: RecipientEditDialogProps): JSX.Element {

    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const openMode = values?.openMode ?? OpenMode.Add;
    const { confirm } = useConfirmation();

    const form = useForm<RecipientEditDialogFormValuesType>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const {
        setValue,
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset,
        watch,
        trigger
    } = form;

    useEffect(() => {
        if (open) {

            if (values) {
                reset(toFormValuesType(values));
            }
        } else {
            reset(defaultFormValues);
        }
    }, [reset, values, open]);

    const [
        recipientType, 
        cloneRecipientUsage,
        linkToRecipientUser,
        userFirstName,
        userLastName,
    ] = watch([
        "type", 
        "cloneRecipientUsage",
        "linkToRecipientUser",
        "userFirstName",
        "userLastName",
    ]);

    const { mutateAsync: checkRecipient } = useQueryNotifications(useCheckRecipient)({
        groupID: routeContext.groupID as number
    });

    const { data: referenceData } = useQueryNotifications(useRecipientReferenceData)({
        groupID: routeContext.groupID as number,
        organizationID: values?.organizationID as number,
        enabled: (cloneRecipientUsage || linkToRecipientUser) && !!values?.organizationID 
    });

    const { data: allowVoiceAlarmAttribute } = useQueryNotifications(useCustomerAttribute)({
        groupID: routeContext.groupID as number,
        name: "allow voice alarm",
        enabled: openMode === OpenMode.Add
    });

    const allowVoiceAlarm = allowVoiceAlarmAttribute
        ? allowVoiceAlarmAttribute.attributeValue.toLowerCase() === "true"
        : false;


    const allRecipients = useMemo(() => {

        if (referenceData) {
            const { recipients, users } = referenceData;

            const userRecipients = Enumerable
                .from(recipients)
                .join(
                    users,
                    o => o.userID,
                    i => i.id,
                    (o, i) => ({ o: o, i: i })
                )
                .select(oi => {
                    const displayText = `[${getRecipientTypeName(oi.o.type)}] ${oi.i?.name}: ${oi.o.to}`;

                    return {
                        recipientID: oi.o.id,
                        displayText: displayText,
                        userID: oi.i.id,
                        userName: oi.i?.name
                    };
                })
                .orderBy(r => r.userName)
                .toArray();

            return userRecipients;
        }

        return [];
    }, [referenceData]);
    

    const existingRecipients = useMemo(() => {

        if (referenceData) {
            const { recipients, users } = referenceData;

            const userRecipients = Enumerable
                .from(recipients)
                .where(r => r.type === recipientType)
                .join(
                    users,
                    o => o.userID,
                    i => i.id,
                    (o, i) => ({ o: o, i: i })
                )
                .select(oi => {
                    const displayText = !recipientType
                        ? `[${getRecipientTypeName(recipientType)}] ${oi.i?.name}: ${oi.o.to}`
                        : `${oi.i?.name}: ${oi.o.to}`;

                    return {
                        recipientID: oi.o.id,
                        displayText: displayText,
                        userID: oi.i.id
                    };
                })
                .orderBy(r => r.displayText)
                .toArray();

            return userRecipients;
        }

        return [];
    }, [referenceData, recipientType]);

    async function onOkay(data: RecipientEditDialogFormValuesType) {

        if (!data.linkToRecipientUser) {
            await checkRecipient(toCheckRecipientParams(data));
        }

        const updatedRecipient = toReturnValues(data);

        onClose({
            reason: "ok",
            values: updatedRecipient
        });
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getCancelConfirmation()
            : true;

        if (canCancel) {
            onClose({ reason: "cancel" });
        }
    }

    async function getCancelConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),

            confirmationText: openMode === OpenMode.Add
                ? t("recipientEditDialog.cancelAddRecipient")
                : t("recipientEditDialog.cancelEditRecipient"),

            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function onTypeChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {

        const type = Number(event.target.value) as RecipientTypeEnum;

        if (type === RecipientTypeEnum.Text || type === RecipientTypeEnum.Voice) {
            setValue("countryCode", "US");
            setValue("subType", RecipientSubTypeEnum.Cell);
            setValue("cloneRecipientID", -1);
        }
    }

    const _validateName = (value: string): boolean => {
        return value.toString().trim().length <= 50;
    }



    useEffect(() => {

        if (!linkToRecipientUser) {
            setTimeout(() => {
                const items: ('userFirstName' | 'userLastName')[] = [];
    
                if ((userFirstName?.trim().length ?? 0) === 0) {
                    items.push('userFirstName');
                }

                if ((userLastName?.trim().length ?? 0) === 0) {
                    items.push('userLastName');
                }

                if (items.length) {
                    trigger(items);
                }
    
            }, 1);
        }
    }
    );

    return (
        <>
            <ResponsiveDialog
                title={openMode === OpenMode.Add ? t("recipientEditDialog.addRecipient") : t("recipientEditDialog.editRecipient")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.okay")}
                open={open}
                onClose={
                    async (reason: string) => {
                        if (reason === "ok") {
                            await handleSubmit(async (data) => onOkay(data))();
                        } else {
                            await onCancel();
                        }
                    }
                }
            >
                <Form form={form}>
                    {values?.allowLinkToRecipientUser && !linkToRecipientUser &&
                    <FormPropertySection label=''>

                        <FormProperty>
                            <FormInput
                                name="userFirstName"
                                label={t('properties.firstName')}
                                sx={{
                                    width: 1,
                                    marginTop: 1,
                                }}

                                rules={{
                                    required: {
                                        value: true,
                                        message:t('properties.valueRequired', {item: t('properties.firstName')})
                                    },
                                    validate: (value: string) => !_validateName(value) ?
                                    t('properties.maxFieldLength', {field: t('properties.firstName'), max: 50}) : 
                                    true
                                }}
                            />

                        </FormProperty>


                        <FormProperty>
                            <FormInput
                                name="userLastName"
                                label={t('properties.lastName')}
                                sx={{
                                    width: 1,
                                    marginTop: 1,
                                }}


                                rules={{
                                    required: {
                                        value: true,
                                        message:t('properties.valueRequired', {item: t('properties.lastName')})
                                    },
                                    validate: (value: string) => !_validateName(value) ?
                                    t('properties.maxFieldLength', {field: t('properties.lastName'), max: 50}) : 
                                    true
                                }}

                            />

                        </FormProperty>


                    </FormPropertySection>
                    }

                    <FormPropertySection label="">
                        <FormProperty>
                            <FormSelect
                                name="type"
                                label={t("recipientEditDialog.type")}
                                sx={{
                                    width: 1
                                }}
                                disabled={openMode === OpenMode.Edit}
                                onChange={onTypeChange}
                            >
                                {recipientTypes.filter(o =>
                                    openMode === OpenMode.Edit ||
                                    allowVoiceAlarm ||
                                    o.id !== RecipientTypeEnum.Voice).map(o =>
                                        <MenuItem key={o.id} value={o.id}>
                                            {o.name}
                                        </MenuItem>
                                    )}
                            </FormSelect>
                        </FormProperty>

                        {/* {(recipientType === RecipientTypeEnum.Text || recipientType === RecipientTypeEnum.Voice) && */}
                        {(recipientType === RecipientTypeEnum.Voice) &&
                            <FormProperty>
                                <FormSelect
                                    name="subType"
                                    label={t("recipientEditDialog.subType")}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={openMode === OpenMode.Edit}
                                >
                                    {recipientSubTypes.map(o =>
                                        <MenuItem key={o.id} value={o.id}>
                                            {o.name}
                                        </MenuItem>
                                    )}
                                </FormSelect>
                            </FormProperty>
                        }
                    </FormPropertySection>

                    {recipientType === RecipientTypeEnum.Email &&
                        <FormPropertySection label="">
                            <FormProperty>
                                <FormInput
                                    name="emailAddress"
                                    label={t("recipientEditDialog.emailAddress")}
                                    sx={{
                                        width: 1
                                    }}
                                    rules={{
                                        pattern: {
                                            value: /^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/,
                                            message: t("userEditor.steps.general.emailRequired")
                                        },
                                        required: recipientType === RecipientTypeEnum.Email
                                            ? t("userEditor.steps.general.emailRequired")
                                            : false
                                    }}
                                />
                            </FormProperty>
                        </FormPropertySection>
                    }

                    {(recipientType === RecipientTypeEnum.Text || recipientType === RecipientTypeEnum.Voice) &&
                        <FormPropertySection label="">
                            <FormProperty>
                                <FormSelect
                                    name="countryCode"
                                    label={t("recipientEditDialog.country")}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={openMode === OpenMode.Edit || recipientType === RecipientTypeEnum.Voice}
                                >
                                    {countries.map(o =>
                                        <MenuItem key={o.code} value={o.code}>
                                            {`${o.name} (+${o.telephonePrefix})`}
                                        </MenuItem>
                                    )}
                                </FormSelect>
                            </FormProperty>

                            <FormProperty>
                                <FormInput
                                    name="phoneNumber"
                                    label={t("recipientEditDialog.phoneNumber")}
                                    rules={{
                                        pattern: {
                                            // Bug 52761: DOC - Add User Error
                                            value: phoneNumberWithoutCountryCodeRegex,
                                            // value: /^\(\d{3}\) ?\d{3}( |-)?\d{4}|^\d{3}( |-)?\d{3}( |-)?\d{4}/,
                                            message: t('profile.invalidPhone')
                                        },
                                        required: recipientType === RecipientTypeEnum.Text || recipientType === RecipientTypeEnum.Voice
                                            ? t("userEditor.steps.general.phoneRequired")
                                            : false
                                    }}
                                    sx={{
                                        width: 1
                                    }}
                                />
                            </FormProperty>
                        </FormPropertySection>
                    }


                    {values?.allowLinkToRecipientUser === true ?  
                        <FormPropertySection label="">
                            <FormCheckbox name="linkToRecipientUser" label={t('recipientEditDialog.linkToExistingRecipientUser')} />
                        </FormPropertySection> :
                    values?.allowCloneRecipientUsage === true ?                
                        <FormPropertySection label="">
                            <FormCheckbox name="cloneRecipientUsage" label={t('recipientEditDialog.cloneRecipientUsage')} />
                        </FormPropertySection> :
                        null
                    }

                    {cloneRecipientUsage &&
                        <FormPropertySection label="">
                            <FormProperty sx={{
                                width: 1,
                                marginTop: 1
                            }}>
                                <FormSelect
                                    name="cloneRecipientID"
                                    label={t("recipientEditDialog.recipients")}
                                    sx={{
                                        width: 1
                                    }}
                                    rules={{
                                        validate: (value: number) => value !== -1
                                    }}
                                >
                                    <MenuItem key={-1} value={-1} disabled={true}>
                                        Select an existing recipient...
                                    </MenuItem>
                                    {existingRecipients.map((o, index) =>
                                        <MenuItem key={index} value={o.recipientID}>
                                            {o.displayText}
                                        </MenuItem>
                                    )}
                                </FormSelect>
                            </FormProperty>
                        </FormPropertySection>
                    }

                    {linkToRecipientUser &&
                        <FormPropertySection label="">
                            <FormProperty sx={{
                                width: 1,
                                marginTop: 1
                            }}>
                                <FormSelect
                                    name="linkedRecipientUserID"
                                    label={t("recipientEditDialog.recipients")}
                                    sx={{
                                        width: 1
                                    }}
                                    rules={{
                                        validate: (value: number) => value !== -1
                                    }}
                                >
                                    <MenuItem key={-1} value={-1} disabled={true}>
                                        Select an existing recipient...
                                    </MenuItem>
                                    {allRecipients.map((o, index) =>
                                        <MenuItem key={index} value={o.userID}>
                                            {`${o.displayText}`}
                                        </MenuItem>
                                    )}
                                </FormSelect>
                            </FormProperty>
                        </FormPropertySection>
                    }

                </Form>
            </ResponsiveDialog>
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getCancelConfirmation}
            />
        </>
    );
}

export type RecipientEditDialogState = DialogState<RecipientEditDialogInitValues, RecipientEditDialogResult>;
export type BindRecipientEditDialogReturn = BindDialogReturn<RecipientEditDialogInitValues, RecipientEditDialogResult>;

export function useRecipientEditDialogState(): RecipientEditDialogState {
    return useDialogState();
}

export function bindRecipientEditDialog(props: RecipientEditDialogState): BindRecipientEditDialogReturn {
    return bindDialog(props);
}
